import * as React from 'react';
import { useEffect, useState } from 'react';
import { cn } from '../../lib/cn';

export type AvatarColorVariant =
  | 'blue'
  | 'green'
  | 'amber'
  | 'rose'
  | 'violet'
  | 'slate'
  | 'teal';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * 'circle' (default) is a full circle — the original, unchanged look for
 * every existing caller (users, contacts, etc).
 *
 * 'square' is a hard 1:1 square with no rounding: the image is the entire
 * avatar, scaled to fit the box exactly, with no container drawn around it.
 * Used for firm/company logos, which are validated square at upload time —
 * a logo is artwork with its own silhouette, so framing it in a rounded,
 * tinted box would clip and re-brand it.
 */
export type AvatarShape = 'circle' | 'square';

const SHAPE_BORDER_RADIUS: Record<AvatarShape, number> = {
  circle: 9999,
  square: 0,
};

// Size table mirrors Gobi's HTML `.avatar-{xs,sm,md,lg,xl}` exactly:
//   xs:  1.25rem (20px) · font 9px
//   sm:  1.75rem (28px) · font 11px
//   md:  2.25rem (36px) · font 12px
//   lg:  3rem    (48px) · font 14px
//   xl:  4rem    (64px) · font 18px
const SIZE_STYLES: Record<AvatarSize, React.CSSProperties> = {
  xs: { width: 20, height: 20, fontSize: 9, fontWeight: 600 },
  sm: { width: 28, height: 28, fontSize: 11, fontWeight: 600 },
  md: { width: 36, height: 36, fontSize: 12, fontWeight: 600 },
  lg: { width: 48, height: 48, fontSize: 14, fontWeight: 600 },
  xl: { width: 64, height: 64, fontSize: 18, fontWeight: 600 },
};

// Tint values mirror Gobi's HTML mocks (avatar-tint-*) exactly so the React
// implementation visually matches the Astro/landing components 1:1.
// See Downloads/quotes.html · client_overview.html · client_overview_1.html.
// Non-slate entries use identity tint palette colours — data-viz scale, not design tokens.
const COLOR_STYLES: Record<AvatarColorVariant, React.CSSProperties> = {
  blue: { background: '#DBEAFE', color: '#1E40AF' }, // data-viz scale, not a design token
  green: { background: '#DCFCE7', color: '#166534' }, // data-viz scale, not a design token
  amber: { background: '#FEF3C7', color: '#92400E' }, // data-viz scale, not a design token
  rose: { background: '#FFE4E6', color: '#9F1239' }, // data-viz scale, not a design token
  violet: { background: '#EDE9FE', color: '#5B21B6' }, // data-viz scale, not a design token
  slate: { background: 'var(--line)', color: 'var(--accent)' },
  teal: { background: '#CCFBF1', color: '#115E59' }, // data-viz scale, not a design token
};

// Distinct identity palette for hash-based color assignment.
// data-viz scale, not design tokens — perceptually distinct hues for avatar identification.
const AVATAR_PALETTE: readonly [string, ...string[]] = [
  '#E05D9B', // data-viz scale, not a design token — pink
  '#5D9BE0', // data-viz scale, not a design token — blue
  '#9B5DE0', // data-viz scale, not a design token — purple
  '#5DE09B', // data-viz scale, not a design token — teal
  '#E09B5D', // data-viz scale, not a design token — orange
  '#5DE0E0', // data-viz scale, not a design token — cyan
  '#E05D5D', // data-viz scale, not a design token — red
  '#9BE05D', // data-viz scale, not a design token — lime
];

/**
 * Derives initials from a display name.
 * "Jane Doe" → "JD", "Alice" → "A", "" → "?"
 */
export function getInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return parts[0]?.charAt(0).toUpperCase() ?? '?';
  }
  const first = parts[0]?.charAt(0).toUpperCase() ?? '';
  const last = parts[parts.length - 1]?.charAt(0).toUpperCase() ?? '';
  return first + last;
}

/**
 * Alias for {@link getInitials} — matches the local web component's export name
 * so callers can import either name from `@jumpstone/ui-react`.
 */
export const name_to_initials = getInitials;

/**
 * Generate a consistent avatar background color from an arbitrary string
 * (e.g. user ID, email, display name). Returns a hex color from the palette.
 */
export function string_to_color(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

interface AvatarNameProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Display name — used to derive initials when no image src is provided */
  name: string;
  /** Explicit initials override — when omitted, derived from `name` via getInitials */
  initials?: never;
  /**
   * Hex background color override for the no-image fallback (bold
   * background + white text, e.g. from `string_to_color(name)` — matches
   * the identity-hash palette used elsewhere for org/entity avatars).
   * When omitted, falls back to the softer `colorVariant` pastel palette.
   */
  color?: string;
  colorVariant?: AvatarColorVariant;
  size?: AvatarSize;
  /** 'circle' (default) or 'square' (rounded square) */
  shape?: AvatarShape;
  /** Optional image URL. When provided, renders an <img> instead of initials. */
  src?: string;
}

interface AvatarInitialsProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Explicit initials string (e.g. from name_to_initials) */
  initials: string;
  /** Hex background color (e.g. from string_to_color) */
  color?: string;
  name?: never;
  colorVariant?: never;
  size?: AvatarSize;
  /** 'circle' (default) or 'square' (rounded square) */
  shape?: AvatarShape;
  src?: never;
}

export type AvatarProps = AvatarNameProps | AvatarInitialsProps;

function Avatar({ size = 'md', className, style, ...rest }: AvatarProps) {
  const sizeStyle = SIZE_STYLES[size];

  // Track image-load failure so a broken/404'ing `src` falls back to the
  // initials chip instead of a broken-image icon. Only the name-based mode
  // renders an <img>; the initials-only mode never sets `src`, so this stays
  // inert there. Hooks must run unconditionally, so it lives at the top.
  const srcForFallback = 'src' in rest ? rest.src : undefined;
  const [imgFailed, setImgFailed] = useState(false);
  // Reset on src change so switching to a working image after a prior failure
  // (or between two different avatars) re-attempts the load rather than
  // latching on the stale failure.
  useEffect(() => {
    setImgFailed(false);
  }, [srcForFallback]);

  if ('initials' in rest && rest.initials !== undefined) {
    // Explicit initials + optional hex color mode (local avatar API compat)
    const { initials, color, shape = 'circle', ...divProps } = rest;
    const baseStyle: React.CSSProperties = {
      ...sizeStyle,
      ...(color ? { background: color, color: '#ffffff' } : {}),
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: SHAPE_BORDER_RADIUS[shape],
      userSelect: 'none',
      WebkitUserSelect: 'none',
      flexShrink: 0,
      overflow: 'hidden',
      fontWeight: 600,
      ...style,
    };
    return (
      <div className={cn(className)} style={baseStyle} {...divProps}>
        <span style={{ lineHeight: 1 }}>{initials}</span>
      </div>
    );
  }

  // Name-based mode (original lib API)
  const {
    name,
    colorVariant = 'blue',
    color,
    shape = 'circle',
    src,
    ...divProps
  } = rest as AvatarNameProps;
  // An explicit `color` (bold background + white text) takes precedence
  // over the softer `colorVariant` pastel palette — used when the caller
  // wants identity-hash-style visual weight (e.g. matching an adjacent
  // org/entity avatar) rather than the muted default.
  const colorStyle: React.CSSProperties = color
    ? { background: color, color: '#ffffff' }
    : COLOR_STYLES[colorVariant];
  // A failed image load is treated exactly like "no src": the coloured
  // background + initials fallback returns.
  const showImage = !!src && !imgFailed;
  const baseStyle: React.CSSProperties = {
    ...sizeStyle,
    ...(showImage ? {} : colorStyle),
    display: 'inline-grid',
    placeItems: 'center',
    borderRadius: SHAPE_BORDER_RADIUS[shape],
    userSelect: 'none',
    WebkitUserSelect: 'none',
    flexShrink: 0,
    overflow: 'hidden',
    // Subtle highlight from Gobi's .avatar rule
    boxShadow: showImage ? undefined : '0 1px 0 rgba(255,255,255,0.5) inset',
    ...style,
  };

  return (
    <div className={cn(className)} style={baseStyle} {...divProps}>
      {showImage ? (
        <img
          src={src}
          alt={name}
          onError={() => {
            setImgFailed(true);
          }}
          style={{
            width: '100%',
            height: '100%',
            // A logo must be shown whole — 'contain' fits it inside the box
            // rather than cropping to fill. Photographic circle avatars keep
            // the cover crop, where filling the frame is what you want.
            objectFit: shape === 'square' ? 'contain' : 'cover',
          }}
        />
      ) : (
        <span style={{ lineHeight: 1 }}>{getInitials(name)}</span>
      )}
    </div>
  );
}

export { Avatar };
